
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, ImageOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatImageUrl } from "@/hooks/use-uploads";

interface CountyCardProps {
  county: string;
  count: number;
  imageUrl?: string;
  className?: string;
}

export function CountyCard({
  county,
  count,
  imageUrl,
  className,
}: CountyCardProps) {
  const [imageError, setImageError] = useState(false);
  // Create slug from county name
  const slug = county.toLowerCase().replace(/\s+/g, "-");
  
  // Generate a consistent color based on the county name
  const generateBackgroundColor = (name: string) => {
    const colors = [
      "bg-blue-600", "bg-green-600", "bg-purple-600", 
      "bg-orange-600", "bg-teal-600", "bg-indigo-600",
      "bg-pink-600", "bg-cyan-600", "bg-amber-600"
    ];
    
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Handle image URLs properly
  const getImageUrl = () => {
    // Special case for Wicklow - use the new image
    if (county === "Wicklow") {
      return "/lovable-uploads/65140e30-07e5-4921-8fe5-84402378787b.png";
    }
    
    // If the URL is already a full URL (including http or https), return it directly
    if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
      return imageUrl;
    }
    
    // If we have a local URL but it doesn't start with a slash, add one
    if (imageUrl && !imageUrl.startsWith('/')) {
      return `/${imageUrl}`;
    }
    
    // Return the local URL as is if it has a leading slash
    if (imageUrl) {
      return imageUrl;
    }
    
    // Use unsplash fallback with county name as query
    return `https://source.unsplash.com/featured/?ireland,county,${slug}`;
  };

  console.log(`County: ${county}, Image URL: ${imageUrl}, Processed URL: ${getImageUrl()}`);

  return (
    <Link 
      to={`/nursing-homes/${slug}`}
      className={cn(
        "group block overflow-hidden rounded-lg bg-white border border-grandpal-beige shadow-sm hover:shadow-md transition-all duration-300",
        className
      )}
    >
      <div className="relative h-48 overflow-hidden">
        {!imageError ? (
          <img
            src={getImageUrl()}
            alt={`Nursing homes in ${county}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={() => {
              console.error(`Error loading image for ${county}: ${getImageUrl()}`);
              setImageError(true);
            }}
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${generateBackgroundColor(county)}`}>
            <div className="text-center p-4">
              <ImageOff className="h-12 w-12 text-white mx-auto mb-2 opacity-70" />
              <h3 className="text-2xl font-bold text-white">{county}</h3>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col items-center justify-end p-6">
          <h3 className="text-3xl font-semibold text-white text-center mb-2">{county}</h3>
        </div>
      </div>
      <div className="p-4 flex justify-center items-center">
        <div className="flex items-center text-grandpal-gray">
          <MapPin className="h-4 w-4 mr-1" />
          <span className="text-center">{count} nursing homes</span>
        </div>
        <ArrowRight className="h-5 w-5 text-grandpal-green opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ml-2" />
      </div>
    </Link>
  );
}
